import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import { STOperationEndPointLogic } from "../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import { useState } from "react";
import {
  setAccountsForSTOperation,
  setCampaignsWithoutScopeData,
  setIsScopesLoading,
  setIsScopesTabDataFetched,
  setNegativesInListAndInAdGroupData,
  setNegativesInTheCampaignData,
  setPage2,
  setPage3,
  setRemoveNegativeLoadingCampaignLoading,
  setRemoveNegativeLoadingForBothLoading,
  setRowsPerPage2,
  setRowsPerPage3,
  setScopesOfSTOperation,
  setSelectedAdgroups,
  setSelectedAdgroupsForCampaign,
  setSelectedCampaigns,
  setSelectedCampaignsForCampaign,
  setSelectedNegativeSearchTerm,
  setSelectedNegativeSearchTermForCampaign,
  setSelectedRowsForNegatives,
  setSelectedRowsForNegativesCampaign,
  setSelectedSingleScopeID,
  setSelectedSTOperationAccount,
  setSelectedSTOperationSharedUid,
  setSingleRowIDPassToModal,
  setSingleRowIDPassToModalForCampaign,
} from "../../../../../features/searchTermOperationSlice";

// Constants
const QUERY_IDS = {
  ACCOUNTS: "5761660895625216",
  CAMPAIGNS_WITHOUT_SCOPE: "5755374237908992",
  NEGATIVE_DEFINITION_BOTH: "5760334488928256",
  NEGATIVE_DEFINITION_CAMPAIGN: "5719366876266496",
} as const;

const ACCOUNT_FILTERS = {
  TRAFFIC_SOURCE_SHARED_UID: 1,
  IS_ACTIVE: 2,
} as const;

type NegativeTermType = "adgroups" | "campaigns";

export const useScopes = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountsLoading, setAccountsLoading] = useState(false);

  const selectedSharedUid = searchParams.get("selectedSite") || "";
  const selectedAccountSharedUid = searchParams.get("account") || "";

  // Clear all state when changing site
  const clearParams = () => {
    setSearchParams({});
    dispatch(setSelectedSTOperationSharedUid(""));
    dispatch(setSelectedSTOperationAccount(""));
    dispatch(setAccountsForSTOperation([]));
    dispatch(setIsScopesTabDataFetched(false));
    dispatch(setScopesOfSTOperation([]));
    dispatch(setCampaignsWithoutScopeData([]));
    dispatch(setNegativesInListAndInAdGroupData([]));
    dispatch(setNegativesInTheCampaignData([]));
    dispatch(setSelectedRowsForNegatives([]));
    dispatch(setSingleRowIDPassToModal(""));
    dispatch(setRemoveNegativeLoadingForBothLoading(false));
    dispatch(setSelectedRowsForNegativesCampaign([]));
    dispatch(setSingleRowIDPassToModalForCampaign(""));
    dispatch(setRemoveNegativeLoadingCampaignLoading(false));
  };

  // Clear state when changing account (preserve site selection)
  const clearParamsForAccountSelection = () => {
    setSearchParams({});
    dispatch(setSelectedSTOperationAccount(""));
    dispatch(setIsScopesTabDataFetched(false));
    dispatch(setScopesOfSTOperation([]));
    dispatch(setCampaignsWithoutScopeData([]));
    dispatch(setNegativesInListAndInAdGroupData([]));
    dispatch(setNegativesInTheCampaignData([]));
    dispatch(setSelectedRowsForNegatives([]));
    dispatch(setSingleRowIDPassToModal(""));
    dispatch(setRemoveNegativeLoadingForBothLoading(false));
    dispatch(setSelectedRowsForNegativesCampaign([]));
    dispatch(setSingleRowIDPassToModalForCampaign(""));
    dispatch(setRemoveNegativeLoadingCampaignLoading(false));
  };

  //clear params of tables(like selected filter, row, pagination and passed id)
  const clearParamsOfTables = () => {
    dispatch(setSelectedNegativeSearchTerm("")); // Reset selected negative search term (2nd table)
    dispatch(setSelectedCampaigns([])); // Reset selected campaigns (2nd table)
    dispatch(setSelectedAdgroups([])); // Reset selected adgroups (2nd table)
    dispatch(setSelectedRowsForNegatives([])); // Reset selected rows (2nd table)
    dispatch(setSingleRowIDPassToModal("")); // selected single row id (2nd table)

    dispatch(setSelectedNegativeSearchTermForCampaign("")); // Reset selected negative search term (3rd table)
    dispatch(setSelectedCampaignsForCampaign([])); // Reset selected campaigns (3rd table)
    dispatch(setSelectedAdgroupsForCampaign([])); // Reset selected adgroups (3rd table)
    dispatch(setSelectedRowsForNegativesCampaign("")); // Reset selected rows (3rd table)
    dispatch(setSingleRowIDPassToModalForCampaign("")); // selected single row id (3rd table)
    dispatch(setSelectedSingleScopeID("")); // Reset selected scope id (edit scope modal)

    // dispatch(setPage(0));
    dispatch(setPage2(0));
    dispatch(setPage3(0));

    //dispatch(setRowsPerPage(10));
    dispatch(setRowsPerPage2(10));
    dispatch(setRowsPerPage3(10));
  };

  // Fetch accounts for selected site
  const getAccountsBasedOnSite = async (site_shared_uid: string) => {
    setAccountsLoading(true);
    try {
      await STOperationEndPointLogic.getAccounts(
        oauthTokenHeader,
        QUERY_IDS.ACCOUNTS,
        site_shared_uid,
        ACCOUNT_FILTERS.TRAFFIC_SOURCE_SHARED_UID,
        ACCOUNT_FILTERS.IS_ACTIVE
      );
    } catch (error) {
      console.error("Error fetching accounts:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch accounts. Please try again.",
          type: "error",
        })
      );
    } finally {
      setAccountsLoading(false);
    }
  };

  // Fetch all scopes tab data in parallel
  const fetchScopesTabData = async (
    site_shared_uid: string,
    account_shared_uid?: string
  ) => {
    dispatch(setIsScopesLoading(true));
    dispatch(setIsScopesTabDataFetched(false));

    try {
      const accountUid = account_shared_uid || "";
      const [
        scopesRes,
        campaignsWithoutScopeRes,
        negativeDefinitionBothRes,
        negativeDefinitionInCampaignRes,
      ] = await Promise.all([
        STOperationEndPointLogic.getScopes(
          oauthTokenHeader,
          site_shared_uid,
          accountUid
        ),
        STOperationEndPointLogic.getCampaignsWithoutScope(
          oauthTokenHeader,
          QUERY_IDS.CAMPAIGNS_WITHOUT_SCOPE,
          site_shared_uid,
          accountUid
        ),
        STOperationEndPointLogic.getNegativeDefinitionBothListAndAdgroup(
          oauthTokenHeader,
          QUERY_IDS.NEGATIVE_DEFINITION_BOTH,
          site_shared_uid,
          accountUid
        ),
        STOperationEndPointLogic.getNegativeDefinitionInCampaign(
          oauthTokenHeader,
          QUERY_IDS.NEGATIVE_DEFINITION_CAMPAIGN,
          site_shared_uid,
          accountUid
        ),
      ]);

      const allResponses = {
        scopes: scopesRes,
        campaignsWithoutScope: campaignsWithoutScopeRes,
        negativeDefinitionBoth: negativeDefinitionBothRes,
        negativeDefinitionInCampaign: negativeDefinitionInCampaignRes,
      };

      return allResponses;
    } catch (error) {
      console.error("Error fetching scopes:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch scopes data. Please try again.",
          type: "error",
        })
      );
    } finally {
      dispatch(setIsScopesLoading(false));
      dispatch(setIsScopesTabDataFetched(true));
    }
  };

  const handleClearParams = async (type: NegativeTermType) => {
    // Clear selections and refetch data based on type
    if (type === "adgroups") {
      dispatch(setSelectedRowsForNegatives([]));
      dispatch(setSingleRowIDPassToModal(""));

      // Refetch adgroup data
      await STOperationEndPointLogic.getNegativeDefinitionBothListAndAdgroup(
        oauthTokenHeader,
        QUERY_IDS.NEGATIVE_DEFINITION_BOTH,
        selectedSharedUid,
        selectedAccountSharedUid
      );

      dispatch(setRemoveNegativeLoadingForBothLoading(false));
    } else if (type === "campaigns") {
      dispatch(setSelectedRowsForNegativesCampaign([]));
      dispatch(setSingleRowIDPassToModalForCampaign(""));

      // Refetch campaign data
      await STOperationEndPointLogic.getNegativeDefinitionInCampaign(
        oauthTokenHeader,
        QUERY_IDS.NEGATIVE_DEFINITION_CAMPAIGN,
        selectedSharedUid,
        selectedAccountSharedUid
      );

      dispatch(setRemoveNegativeLoadingCampaignLoading(false));
    }
  };

  // Handle negative term removal
  const removeNegativeSearchTerm = async (
    reqBody: any,
    onClose: () => void,
    type: NegativeTermType
  ) => {
    try {
      onClose();

      // Set loading state
      if (type === "adgroups") {
        dispatch(setRemoveNegativeLoadingForBothLoading(true));
      }
      if (type === "campaigns") {
        dispatch(setRemoveNegativeLoadingCampaignLoading(true));
      }

      const response = await STOperationEndPointLogic.removeNegativeTerms(
        oauthTokenHeader,
        reqBody
      );

      if (!response) {
        throw new Error(
          "Failed to remove negative terms. Please try again later."
        );
      }

      const { reason, data } = response;

      const summary = data.summary;
      const results = data.results || [];

      // Handle based on reason
      if (reason === "Success") {
        await handleClearParams(type);
        dispatch(
          setDisplayErrorSnackBar({
            message: `Successfully removed ${summary.successful} negative term(s)`,
            type: "success",
          })
        );
      } else if (reason === "Partial Success") {
        await handleClearParams(type);
        // Partial success - some succeeded, some failed
        const failedItems = results.filter((r: any) => r.status === "failed");
        const successfulItems = results.filter(
          (r: any) => r.status === "success"
        );

        // Show detailed error for failed items
        const errorDetails = failedItems
          .map((item: any) => {
            const term = item.term || "Unknown term";
            const error = item.error || "Unknown error";
            return `${term}: ${error}`;
          })
          .join("; ");

        dispatch(
          setDisplayErrorSnackBar({
            message: `Removed ${successfulItems.length}/${summary.total} term(s). ${failedItems.length} failed. ${errorDetails}`,
            type: "warning",
          })
        );

        dispatch(
          setDisplayErrorSnackBar({
            message: `Removed ${successfulItems.length}/${summary.total} term(s). ${failedItems.length} failed. ${errorDetails}`,
            type: "warning",
          })
        );
      }
    } catch (error) {
      // Reset loading state
      if (type === "adgroups") {
        dispatch(setRemoveNegativeLoadingForBothLoading(false));
      }
      if (type === "campaigns") {
        dispatch(setRemoveNegativeLoadingCampaignLoading(false));
      }

      // Extract error message - handle both string and object cases
      let errorMessage = "Failed to remove negative terms. Please try again.";

      const err = error as any;

      if (err?.parsedText?.reason) {
        errorMessage = err.parsedText.reason;
      } else if (err?.response?.data?.reason) {
        errorMessage = err.response.data.reason;
      } else if (typeof err?.message === "string") {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      dispatch(
        setDisplayErrorSnackBar({
          message: errorMessage,
          type: "error",
        })
      );
    }
  };

  //get campaigns without scope
  const campaignsWithoutScope = async () => {
    try {
      await STOperationEndPointLogic.getCampaignsWithoutScope(
        oauthTokenHeader,
        QUERY_IDS.CAMPAIGNS_WITHOUT_SCOPE,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      console.error("Error fetching campaigns without scope:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to refresh campaigns. Please try again.",
          type: "error",
        })
      );
    }
  };

  // Refresh scope list
  const scopeList = async () => {
    try {
      await STOperationEndPointLogic.getScopes(
        oauthTokenHeader,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      console.error("Error fetching scopes:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to refresh scopes. Please try again.",
          type: "error",
        })
      );
    }
  };

  return {
    clearParams,
    clearParamsForAccountSelection,
    clearParamsOfTables,
    accountsLoading,
    getAccountsBasedOnSite,
    fetchScopesTabData,
    removeNegativeSearchTerm,
    campaignsWithoutScope,
    scopeList,
  };
};
